// @flow strict

import React from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Progressbar } from '@ansarada/ace-react';
import queryString from 'query-string';

import ClientExists from './Internal/ClientExists';
import SignUp from './Internal/SignUpForm/SignUpV2';
import Welcome from './Internal/Welcome';
import Question1Space from './Internal/Question1Space';
import Question2Outcome from './Internal/Question2Outcome';
import Question3Stage from './Internal/Question3Stage';
import type {
  QuestionOptionType,
  CustomerInfo,
  referrerOptionType,
  ProvisioningData,
} from './types';
import type { SignupLocalStorageData } from '../FreeTrialThankYou/types';
import { goToProfilePage } from '../FreeTrialThankYou/helpers';
import Question4Submit from './Internal/Question4Submit';
import {
  findSummaryProps,
  generateOptions,
  generateQuestionOptions,
  getIndustry,
  getReferrerPathname,
  getSummaryProps,
} from './helpers';
import industryData from './freetrial-data/industry.json';
import styles from './styles.scss';
import { track } from '../../core/lib/segment-actions';

type PropsType = {|
  signUpTitle: string,
  signUpTitleMS: string,
  signUpSubtitle: string,
  signUpSubtitleMS: string,
  signUpSubtitle2: string,
  welcomeTitle: string,
  welcomeDescription: string,
  welcomeImageDesktop: string,
  welcomeImageMobile: string,
  countryCode?: string,
  recaptchaSiteKey: string,
  questionOptions: Array<QuestionOptionType>,
  question1Text: string,
  question1Placeholder?: string,
  question1ImageDesktop: string,
  question1ImageMobile: string,
  question2Text: string,
  question2Placeholder?: string,
  question2Part2Placeholder?: string,
  question2Part3Placeholder?: string,
  question2ImageDesktop: string,
  question2ImageMobile: string,
  question3Text: string,
  question3Placeholder?: string,
  question3ImageDesktop: string,
  question3ImageMobile: string,
  question4Label: string,
  question4Text: string,
  question4Placeholder?: string,
  question4ImageDesktop: string,
  question4ImageMobile: string,
  restrictedEmails: Array<string>,
  restrictedEmailMessage: string,
  sendGridApiKey: string,
  referrerOptions: Array<referrerOptionType>,
  referrer?: string,
  formImageDesktop?: string,
  formImageMobile?: string,
  useSimplifiedMobileSignUp: boolean,
  enabledMsStartupSignup: boolean,
  enabledStartupConferenceSignup: boolean,
  enabledUpflowy: boolean,
  signupUserId: string,
  enabledTransactionTypeIndustryQuestions: boolean,
  enabledSmsIntegration: boolean,
|};

const FreeTrial = (props: PropsType) => {
  const {
    enabledUpflowy = false,
    signupUserId,
    enabledTransactionTypeIndustryQuestions = false,
    question1Placeholder = 'For example a company or an advisory firm',
    question2Placeholder = 'For example a Capital Raise or Merger',
    question2Part2Placeholder = enabledTransactionTypeIndustryQuestions
      ? 'Choose your industry group'
      : 'Choose a specific area for your outcome',
    question2Part3Placeholder = 'Choose a specific area of your industry',
    question3Placeholder = 'Choose the current stage for your outcome',
    question4Label = 'What else do we need to know?',
    question4Placeholder = 'Enter information about your desired outcome or ask us a question',
    questionOptions,
    enabledSmsIntegration,
  } = props;

  const [step, setStep] = React.useState(0);
  const [stepHistory] = React.useState([]);
  const [question1, setQuestion1] = React.useState('');
  const [question2, setQuestion2] = React.useState('');
  const [question2Part2, setQuestion2Part2] = React.useState('');
  const [question2Part3, setQuestion2Part3] = React.useState('');
  const [question3, setQuestion3] = React.useState('');
  const [customerInfo, setCustomerInfo] = React.useState<?CustomerInfo>(null);
  const [clientExists, setClientExists] = React.useState(false);

  let referrer;
  if (props.referrer) {
    const referrerPath = getReferrerPathname(props.referrer);
    const matchedReferrer = (props.referrerOptions || []).find(
      option => option.referrer === referrerPath,
    );
    if (matchedReferrer) {
      const outcomeSummary =
        findSummaryProps(questionOptions, matchedReferrer.outcomeType, 'question2') ||
        findSummaryProps(questionOptions, matchedReferrer.outcomeSubType, 'question2part2');
      referrer = outcomeSummary && outcomeSummary.pathwayId ? matchedReferrer : undefined;
    }
  }

  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search) {
      const params = queryString.parse(window.location.search);
      if (params.utm_campaign === 'fy22_c_pql_dataroom_freetrial') {
        track('f_website_dataroom-banner.clicked');
      }
    }
  }, []);

  React.useEffect(() => {
    setQuestion2('');
    if (enabledTransactionTypeIndustryQuestions) {
      setQuestion2Part2('');
    }
  }, [question1]);
  React.useEffect(() => {
    if (!referrer) {
      setQuestion2Part2('');
    }
  }, [question2]);
  React.useEffect(() => {
    if (!referrer) {
      setQuestion3('');
    }
    if (enabledTransactionTypeIndustryQuestions) {
      setQuestion2Part3('');
    }
  }, [question2Part2]);

  const question1Options = React.useMemo(() => {
    const firstQuestionOptions = props.questionOptions.filter(q => q.type === 'question1');
    return enabledTransactionTypeIndustryQuestions
      ? generateOptions(firstQuestionOptions).filter(opt => opt.title !== 'Other')
      : generateOptions(firstQuestionOptions);
  }, [props.questionOptions]);

  const secondQuestionOptions = React.useMemo(() => {
    const rs = generateQuestionOptions(questionOptions, 'question2', [question1]);
    if (rs.length === 0) {
      setQuestion2('');
    }
    return rs;
  }, [question1, questionOptions]);

  const secondQuestionOptionsPart2 = React.useMemo(
    () => generateQuestionOptions(questionOptions, 'question2part2', [question2]),
    [question2, questionOptions],
  );

  const thirdQuestionOptions = React.useMemo(() => {
    const rs = generateQuestionOptions(questionOptions, 'question3', [question2, question2Part2]);
    if (rs.length === 0) {
      setQuestion3('');
    }
    return rs;
  }, [question2, question2Part2, questionOptions]);

  const {
    activeIndsutryGroup: secondQuestionIndustryGroup,
    activeSubIndustry,
  } = React.useMemo(() => {
    return getIndustry(industryData);
  }, []);

  const secondQuestionIndustryName = React.useMemo(() => {
    return activeSubIndustry.filter(i => i.id.toString().startsWith(question2Part2.split('|')[0]));
  }, [question2Part2]);

  const thirdQuestionOptionsAll = React.useMemo(() => {
    return generateQuestionOptions(questionOptions, 'question3', ['all']);
  }, []);

  const { outcome, pathwayId } = React.useMemo(
    () => getSummaryProps(questionOptions, question1, question2, question2Part2, question3),
    [questionOptions, question1, question2, question2Part2, question3],
  );

  const goToStep = nextStep => {
    stepHistory.push(step);
    setStep(nextStep);
  };
  const handleNext = () => {
    goToStep(step + 1);
  };
  const handleBack = () => {
    const preStep = stepHistory.pop();
    setStep(preStep);
  };

  const isMsSignupOrStartupConference = () =>
    props.enabledMsStartupSignup || props.enabledStartupConferenceSignup;

  if (customerInfo && clientExists) {
    const title = isMsSignupOrStartupConference() ? props.signUpTitleMS : props.signUpTitle;
    const subtitle = isMsSignupOrStartupConference()
      ? props.signUpSubtitleMS
      : props.signUpSubtitle;

    return (
      <ClientExists
        title={title}
        subtitle={subtitle}
        subtitle2={props.signUpSubtitle2}
        clientName={customerInfo.firstName || customerInfo.email}
      />
    );
  }

  const TOTAL_STEPS = 6;
  const SUBSCRIPTION_STEP = 5;

  const goToOnBoarding = () => {
    if (referrer) {
      setQuestion2(referrer.outcomeType);
      setQuestion2Part2(referrer.outcomeSubType);
      setQuestion3('Actively preparing');
      goToStep(TOTAL_STEPS);
    } else {
      goToStep(1);
    }
  };

  if (!customerInfo || step === 0) {
    return (
      <GoogleReCaptchaProvider reCaptchaKey={props.recaptchaSiteKey} useRecaptchaNet>
        {/* $FlowFixMe */}
        <SignUp
          key="signUp"
          title={props.signUpTitle}
          subtitle={props.signUpSubtitle}
          subtitle2={props.signUpSubtitle2}
          defaultCountry={props.countryCode}
          formImageDesktop={props.formImageDesktop}
          formImageMobile={props.formImageMobile}
          onNext={customerData => {
            setCustomerInfo(customerData);
            goToOnBoarding();
          }}
          onClientExists={customerData => {
            setCustomerInfo(customerData);
            setClientExists(true);
          }}
          restrictedEmails={props.restrictedEmails}
          restrictedEmailMessage={props.restrictedEmailMessage}
          sendGridApiKey={props.sendGridApiKey}
          useSimplifiedMobileSignUp={props.useSimplifiedMobileSignUp}
          enabledMsStartupSignup={props.enabledMsStartupSignup}
          enabledStartupConferenceSignup={props.enabledStartupConferenceSignup}
          enabledUpflowy={enabledUpflowy}
          signupUserId={signupUserId}
          enabledSmsIntegration={enabledSmsIntegration}
        />
      </GoogleReCaptchaProvider>
    );
  }

  const goToThankYouProfilePage = (data: ProvisioningData) => {
    const localData: SignupLocalStorageData = { ...data, userEmail: customerInfo.email };
    goToProfilePage(localData, false);
  };

  return (
    <GoogleReCaptchaProvider reCaptchaKey={props.recaptchaSiteKey} useRecaptchaNet>
      <Progressbar percentValue={(step / TOTAL_STEPS) * 100} className={styles.progressBar} />
      {step === 1 && (
        <Welcome
          title={props.welcomeTitle}
          description={props.welcomeDescription}
          onNext={handleNext}
          bgDesktop={props.welcomeImageDesktop}
          bgMobile={props.welcomeImageMobile}
        />
      )}
      {step === 2 && (
        <Question1Space
          key="Question1Space"
          title={props.question1Text}
          onBack={handleBack}
          onNext={() => {
            if (question1 === 'Student') {
              // Skip outcome and stage questions
              goToStep(TOTAL_STEPS);
            } else {
              handleNext();
            }
          }}
          placeholder={question1Placeholder}
          value={question1}
          onChange={setQuestion1}
          questionOptions={question1Options}
          bgDesktop={props.question1ImageDesktop}
          bgMobile={props.question1ImageMobile}
        />
      )}
      {step === 3 && (
        <Question2Outcome
          key="outcome"
          title={props.question2Text}
          onBack={handleBack}
          onNext={() => {
            if (thirdQuestionOptions.length === 0 && !enabledTransactionTypeIndustryQuestions) {
              goToStep(TOTAL_STEPS);
            } else {
              handleNext();
            }
          }}
          part1={question2}
          part2={question2Part2}
          part3={question2Part3}
          onSetPart1={setQuestion2}
          onSetPart2={setQuestion2Part2}
          onSetPart3={setQuestion2Part3}
          part1Placeholder={question2Placeholder}
          part2Placeholder={question2Part2Placeholder}
          part3Placeholder={question2Part3Placeholder}
          part1QuestionOptions={secondQuestionOptions}
          part2QuestionOptions={
            enabledTransactionTypeIndustryQuestions
              ? secondQuestionIndustryGroup
              : secondQuestionOptionsPart2
          }
          part3QuestionOptions={secondQuestionIndustryName}
          bgDesktop={props.question2ImageDesktop}
          bgMobile={props.question2ImageMobile}
          enabledTransactionTypeIndustryQuestions={enabledTransactionTypeIndustryQuestions}
        />
      )}
      {step === 4 && (
        <Question3Stage
          key="stage"
          title={props.question3Text}
          onBack={handleBack}
          onNext={handleNext}
          placeholder={question3Placeholder}
          value={question3}
          onChange={setQuestion3}
          questionOptions={
            enabledTransactionTypeIndustryQuestions ? thirdQuestionOptionsAll : thirdQuestionOptions
          }
          bgDesktop={props.question3ImageDesktop}
          bgMobile={props.question3ImageMobile}
        />
      )}
      {step === SUBSCRIPTION_STEP && (
        <Question4Submit
          key="submit"
          question4Text={props.question4Text}
          question1={question1}
          question2={question2}
          question2Part2={question2Part2}
          question2Part3={question2Part3}
          question3={question3}
          outcome={outcome}
          pathwayId={pathwayId}
          additionalInformationLabel={question4Label}
          additionalInformationPlaceholder={question4Placeholder}
          question1Title={props.question1Text}
          question1Placeholder={question1Placeholder}
          question1Options={question1Options}
          bgDesktop={props.question4ImageDesktop}
          bgMobile={props.question4ImageMobile}
          customerId={customerInfo.customerId}
          onBack={handleBack}
          onGoToProfilePage={goToThankYouProfilePage}
          onGoToFirstQuestion={() => {
            stepHistory.push(1);
            setStep(2);
          }}
          isFromReferrer={!!referrer}
          enabledTransactionTypeIndustryQuestions={enabledTransactionTypeIndustryQuestions}
        />
      )}
    </GoogleReCaptchaProvider>
  );
};

export default FreeTrial;
